import React, { useEffect, useMemo, useState } from 'react';
import ExcelJS from 'exceljs';
import { useQuery } from '@tanstack/react-query';
import 'moment/locale/ko';
import styled from 'styled-components';
import CustomTable from '../../components/Table';
import Loading from '../../components/Loading';
import SettlementController from '../../controller/SettlementController';
import PartnerController, {
  PartnerReportListType,
  HotelSettleType,
} from '../../controller/PartnerController';
import { useNavigate } from 'react-router-dom';
import ReactDatePicker from 'react-datepicker';
import ko from 'date-fns/locale/ko';

const useHotelSettlement = (year: number, month: number) => {
  return useQuery({
    queryKey: ['hotelSettlement', year, month],
    queryFn: async () => {
      const response = await SettlementController.getHotelMonth({ year, month });
      if (response?.data?.ok) {
        return response.data.data.settlement_list;
      } else {
        throw new Error('useHotelSettlement error');
      }
    },
    enabled: !!year && !!month,
  });
};

const StoreReport = () => {
  const navigate = useNavigate();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
  const nowDate = new Date();
  nowDate.setHours(0, 0, 0, 0);
  // 월간 날짜 선택 함수
  const lastMonday = new Date(nowDate);
  lastMonday.setDate(nowDate.getDate() - ((nowDate.getDay() + 6) % 7));
  const possibleEndDate = new Date(lastMonday);
  possibleEndDate.setDate(lastMonday.getDate() + 6);
  // 주간 날짜 선택 함수
  const getLastCompleteWeekMonday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lastMonday = new Date(today);
    lastMonday.setDate(today.getDate() - ((today.getDay() + 6) % 7));

    const maxSelectableDate = new Date();
    maxSelectableDate.setDate(maxSelectableDate.getDate() - 6);

    return lastMonday <= maxSelectableDate
      ? lastMonday
      : new Date(lastMonday.setDate(lastMonday.getDate() - 7));
  };
  const initialStartDate = getLastCompleteWeekMonday();
  const initialEndDate = new Date(initialStartDate);
  initialEndDate.setDate(initialStartDate.getDate() + 6);
  const [year, setYear] = useState<any>(oneMonthAgo.getFullYear());
  const [month, setMonth] = useState<number>(oneMonthAgo.getMonth() + 1);
  const [tabIdx, setTabIdx] = useState<number>(0);

  const [sortedList, setSortedList] = useState<PartnerReportListType[]>([]);

  const { data, isLoading, error } = useHotelSettlement(year, month);

  const excelHeader = [
    '순서',
    '호텔명',
    '월렌탈료 공급가',
    '월렌탈료 부가세',
    '월렌탈료 합계',
    '클러스터 매출(A)',
    '클러스터 수수료(B)',
    '클러스터 광고수수료(C=A*B)',
    '클러스터 부가세',
    '클러스터 합계',
    '객실 관리팁 결제 금액(A)',
    '객실 관리팁 수수료(B)',
    '객실 관리팁 매출(A-B)',
    '객실 관리팁 부가세',
    '객실 관리팁 합계',
    '객실 판매 상품 결제 금액(A)',
    '객실 판매 상품 결제 수수료(B)',
    '객실 판매 상품 매출(A-B)',
    '객실 판매 상품 부가세',
    '객실 판매 상품 합계',
    '장비지원금 회차',
    '장비지원금 공급가',
    '장비지원금 부가세',
    '장비지원금 합계',
    '최종 정산금 공급가',
    '최종 정산금 부가세',
    '최종 정산금 합계',
    '세금계산서 발행 요청 금액(VAT포함)',
    '계좌주',
    '계좌번호',
    '은행명',
    '대표자명',
    '전화번호',
    '이메일',
  ];

  const total = data?.reduce(
    (acc: HotelSettleType, cur: HotelSettleType) => {
      acc.rental_supply += cur.rental_supply;
      acc.rental_vat += cur.rental_vat;
      acc.rental_fee += cur.rental_fee;
      acc.ad_amount += cur.ad_amount;
      acc.ad_fee_rate += Number(cur.ad_fee_rate);
      acc.ad_fee_supply += cur.ad_fee_supply;
      acc.ad_fee_vat += cur.ad_fee_vat;
      acc.ad_fee += cur.ad_fee;
      acc.room_tip_amount += cur.room_tip_amount;
      acc.room_tip_pay_fee += cur.room_tip_pay_fee;
      acc.direct_fee_supply += cur.direct_fee_supply;
      acc.direct_fee_vat += cur.direct_fee_vat;
      acc.direct_fee += cur.direct_fee;
      acc.device_support_supply += cur.device_support_supply;
      acc.device_support_vat += cur.device_support_vat;
      acc.device_support_fee += cur.device_support_fee;
      acc.final_supply += cur.final_supply;
      acc.final_vat += cur.final_vat;
      acc.final_fee += cur.final_fee;
      acc.tax_bill += cur.tax_bill;
      return acc;
    },
    {
      rental_supply: 0,
      rental_vat: 0,
      rental_fee: 0,
      ad_amount: 0,
      ad_fee_rate: 0,
      ad_fee_supply: 0,
      ad_fee_vat: 0,
      ad_fee: 0,
      room_tip_amount: 0,
      room_tip_pay_fee: 0,
      direct_fee_supply: 0,
      direct_fee_vat: 0,
      direct_fee: 0,
      device_support_supply: 0,
      device_support_vat: 0,
      device_support_fee: 0,
      final_supply: 0,
      final_vat: 0,
      final_fee: 0,
      tax_bill: 0,
    },
  );

  const renderTableItem = () => {
    return (
      <>
        {data &&
          data.length > 0 &&
          data.map((data: HotelSettleType, dataIndex: number) => (
            <tr className="dataTableWrap" key={dataIndex}>
              <td>{dataIndex + 1}</td>
              <td>{data.store_name}</td>
              <td>{data.rental_supply.toLocaleString()}원</td>
              <td>{data.rental_vat.toLocaleString()}원</td>
              <td>{data.rental_fee.toLocaleString()}원</td>
              <td>{data.ad_amount.toLocaleString()}원</td>
              <td>{Math.round(Number(data.ad_fee_rate) * 100)}%</td>
              <td>{data.ad_fee_supply.toLocaleString()}원</td>
              <td>{data.ad_fee_vat.toLocaleString()}원</td>
              <td>{data.ad_fee.toLocaleString()}원</td>
              <td>{data.room_tip_amount.toLocaleString()}원</td>
              <td>{data.room_tip_pay_fee.toLocaleString()}원</td>
              <td>{data.direct_fee_supply.toLocaleString()}원</td>
              <td>{data.direct_fee_vat.toLocaleString()}원</td>
              <td>{data.direct_fee.toLocaleString()}원</td>
              <td>{data.room_tip_amount.toLocaleString()}원</td>
              <td>{data.room_tip_pay_fee.toLocaleString()}원</td>
              <td>{data.direct_fee_supply.toLocaleString()}원</td>
              <td>{data.direct_fee_vat.toLocaleString()}원</td>
              <td>{data.direct_fee.toLocaleString()}원</td>
              <td>
                {data.device_support_status === 1 ? data.device_support_round + '회' : '미지원'}
              </td>
              <td>{data.device_support_supply.toLocaleString()}원</td>
              <td>{data.device_support_vat.toLocaleString()}원</td>
              <td>{data.device_support_fee.toLocaleString()}원</td>
              <td>{data.final_supply.toLocaleString()}원</td>
              <td>{data.final_vat.toLocaleString()}원</td>
              <td>{data.final_fee.toLocaleString()}원</td>
              <td>{data.tax_bill.toLocaleString()}원</td>
              <td>{data.account_holder}</td>
              <td>{data.account_nm}</td>
              <td>{data.bank_name}</td>
              <td>{data.full_name}</td>
              <td>{data.phone_number}</td>
              <td>{data.email}</td>
            </tr>
          ))}
        {data.length > 0 ? (
          <tr className="totalTableWrap">
            <td colSpan={2}>합계</td>
            <td>{total.rental_supply.toLocaleString()}원</td>
            <td>{total.rental_vat.toLocaleString()}원</td>
            <td>{total.rental_fee.toLocaleString()}원</td>
            <td>{total.ad_amount.toLocaleString()}원</td>
            <td>-</td>
            <td>{total.ad_fee_supply.toLocaleString()}원</td>
            <td>{total.ad_fee_vat.toLocaleString()}원</td>
            <td>{total.ad_fee.toLocaleString()}원</td>
            <td>{total.room_tip_amount.toLocaleString()}원</td>
            <td>{total.room_tip_pay_fee.toLocaleString()}원</td>
            <td>{total.direct_fee_supply.toLocaleString()}원</td>
            <td>{total.direct_fee_vat.toLocaleString()}원</td>
            <td>{total.direct_fee.toLocaleString()}원</td>
            <td>{total.room_tip_amount.toLocaleString()}원</td>
            <td>{total.room_tip_pay_fee.toLocaleString()}원</td>
            <td>{total.direct_fee_supply.toLocaleString()}원</td>
            <td>{total.direct_fee_vat.toLocaleString()}원</td>
            <td>{total.direct_fee.toLocaleString()}원</td>
            <td>-</td>
            <td>{total.device_support_supply.toLocaleString()}원</td>
            <td>{total.device_support_vat.toLocaleString()}원</td>
            <td>{total.device_support_fee.toLocaleString()}원</td>
            <td>{total.final_supply.toLocaleString()}원</td>
            <td>{total.final_vat.toLocaleString()}원</td>
            <td>{total.final_fee.toLocaleString()}원</td>
            <td>{total.tax_bill.toLocaleString()}원</td>
            <td colSpan={6}></td>
          </tr>
        ) : (
          <p className="noneData">데이터 없음</p>
        )}
      </>
    );
  };

  const excelDownloadClick = () => {
    if (data && total) {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('정산 내역');

      // 헤더 추가
      worksheet.addRow(excelHeader);

      // 데이터 행 추가
      data.forEach((item: HotelSettleType, index: number) => {
        worksheet.addRow([
          index + 1,
          item.store_name,
          item.rental_supply,
          item.rental_vat,
          item.rental_fee,
          item.ad_amount,
          `${Math.round(Number(item.ad_fee_rate) * 100)}%`,
          item.ad_fee_supply,
          item.ad_fee_vat,
          item.ad_fee,
          item.room_tip_amount,
          item.room_tip_pay_fee,
          item.direct_fee_supply,
          item.direct_fee_vat,
          item.direct_fee,
          item.room_tip_amount,
          item.room_tip_pay_fee,
          item.direct_fee_supply,
          item.direct_fee_vat,
          item.direct_fee,
          item.device_support_status === 1 ? `${item.device_support_round}회` : '미지원',
          item.device_support_supply,
          item.device_support_vat,
          item.device_support_fee,
          item.final_supply,
          item.final_vat,
          item.final_fee,
          item.tax_bill,
          item.account_holder,
          item.account_nm,
          item.bank_name,
          item.full_name,
          item.phone_number,
          item.email,
        ]);
      });

      // 합계 행 추가
      worksheet.addRow([
        '합계',
        '',
        total.rental_supply,
        total.rental_vat,
        total.rental_fee,
        total.ad_amount,
        '-',
        total.ad_fee_supply,
        total.ad_fee_vat,
        total.ad_fee,
        total.room_tip_amount,
        total.room_tip_pay_fee,
        total.direct_fee_supply,
        total.direct_fee_vat,
        total.direct_fee,
        total.room_tip_amount,
        total.room_tip_pay_fee,
        total.direct_fee_supply,
        total.direct_fee_vat,
        total.direct_fee,
        '-',
        total.device_support_supply,
        total.device_support_vat,
        total.device_support_fee,
        total.final_supply,
        total.final_vat,
        total.final_fee,
        total.tax_bill,
        '',
        '',
        '',
        '',
        '',
        '',
      ]);

      // 파일 저장
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `호텔 정산 내역_${year}년${month}월.xlsx`);
        document.body.appendChild(link);
        link.click();
      });
    }
  };
  if (isLoading) {
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Loading />
      </div>
    );
  }

  return (
    <Style>
      <div className="rightContentWrap">
        <button className="backButton" onClick={() => navigate('/dashboard')}>
          <img src={`${process.env.PUBLIC_URL}/svg/ic_home.svg`} alt="뒤로가기버튼" />
        </button>
        <h2>
          {year}년 {month}월 호텔정산
        </h2>
        <div className="searchWrap">
          <ReactDatePicker
            selected={month ? new Date(year, month - 1) : null}
            onChange={(date) => {
              if (date) {
                setMonth(date.getMonth() + 1);
                setYear(date.getFullYear());
              }
            }}
            dateFormat="yyyy년 MM월"
            locale={ko}
            maxDate={oneMonthAgo}
            showMonthYearPicker
            onFocus={(e) => {
              e.preventDefault();
              e.target.blur();
            }}
            className="datePicker"
          />
          <div className="buttonsWrap">
            <button onClick={excelDownloadClick} className="excelButton">
              액셀 다운로드
            </button>
          </div>
        </div>

        {data && (
          <div style={{ flexShrink: 0 }}>
            <CustomTable
              tableHeader={excelHeader}
              renderTableItem={renderTableItem}
              type="border"
              dataList={data}
              setSortedList={setSortedList}
            />
          </div>
        )}
      </div>
    </Style>
  );
};

export default StoreReport;

const Style = styled.div`
  p {
    padding: 0;
    margin: 0;
  }

  .rightContentWrap {
    min-width: 100%;
    width: fit-content;
    min-height: 100vh;
    background: #ffffff;
    padding: 2%;
  }

  /* searchWrap */
  .searchWrap {
    display: flex;
    align-items: center;
    /* justify-content: stretch; */
    gap: 10px;
    margin-top: 30px;
    margin-bottom: 20px;

    .css-b62m3t-container {
      width: 400px;
    }
    input {
      width: 150px;
      padding: 9px 10px;
      border-radius: 4px;
      border: 1px solid hsl(0, 0%, 70%);
      outline: none;
    }
    .searchButton {
      margin-left: 10px;
      padding: 9px 20px;
      background: #000;
      color: #fff;
      border-radius: 4px;
      border: none;
      outline: none;
    }
  }
  /* buttonsWrap */
  .buttonsWrap {
    display: flex;
    align-items: center;
    gap: 10px;
    align-items: flex-start;

    button {
      padding: 9px 20px;
      border-radius: 4px;
      border: none;
      outline: none;
      background: #e7e7e7;
      margin-right: 4px;
    }
    .excelButton {
      background: #40977f;
      color: #fff;
    }
  }

  /* datepicker */
  .react-datepicker__month-text {
    padding: 6px 0 4px 0;
  }

  /* table */

  .normalTable thead tr {
    font-size: 14px;
    line-height: 18px;
    white-space: nowrap;
  }
  .normalTable tbody td {
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    text-align: center;

    img {
      width: 16px;
      cursor: pointer;
    }
  }
  .dataTableWrap.errorStore {
    background: #fff4f4;
  }
   {
    border-right: thick double #bbb;
  }
   {
    border-right: thick double #bbb;
  }
   {
    border-right: thick double #bbb;
  }
   {
    border-right: thick double #bbb;
  }
  .normalTable th:nth-child(28),
  .dataTableWrap td:nth-child(28),
  .normalTable th:nth-child(27),
  .dataTableWrap td:nth-child(27),
  .normalTable th:nth-child(24),
  .dataTableWrap td:nth-child(24),
  .normalTable th:nth-child(20),
  .dataTableWrap td:nth-child(20),
  .normalTable th:nth-child(15),
  .dataTableWrap td:nth-child(15),
  .normalTable th:nth-child(10),
  .dataTableWrap td:nth-child(10),
  .normalTable th:nth-child(5),
  .dataTableWrap td:nth-child(5) {
    border-right: thick double #bbb;
  }
  .totalTableWrap td:nth-child(4),
  .totalTableWrap td:nth-child(9),
  .totalTableWrap td:nth-child(14),
  .totalTableWrap td:nth-child(19),
  .totalTableWrap td:nth-child(23),
  .totalTableWrap td:nth-child(26),
  .totalTableWrap td:nth-child(27) {
    border-right: thick double #bbb;
  }
  .totalTableWrap td {
    background: #ddd;
  }

  .nullText {
    color: #999;
  }
  thead {
    position: sticky;
    top: -1px;
  }

  /* guideWrap */
  .guideWrap {
    z-index: 2;
    margin-top: 10px;
    margin-bottom: 10px;
    padding: 10px 20px;
    border-radius: 5px;
    border: 1px solid #cecece;
    background: #fcfcfc;
    position: absolute;
    left: 2vw;

    h3 {
      color: #979797;
      margin: 10px 0 4px 0;
      font-size: 14px;
      font-weight: 500;
    }
    p {
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
    }
    .pointText p {
      font-weight: 500;
      color: #ff6060;
    }
  }
  .noneData {
    padding: 10px 16px;
    min-width: 100%;
    text-align: center;
    white-space: nowrap;
  }
  .checkDate {
    color: #ff6060;
  }

  @media (max-width: 1400px) {
    .totalTable {
      width: 50%;
    }
  }
`;
